import { $Q } from "./querySelector"
let lastTrigger = null
// Element to give focus back to when an overlay panel (side cart) closes
let overlayOpener = null
let removeFocusTrap = null

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Remember the control that opened an overlay panel, when the opening is not
 * a direct click on it (e.g. add to cart opens the side cart).
 * @param {HTMLElement} element
 */
export const setOverlayOpener = (element) => {
  overlayOpener = element || null
}

const getFocusableElements = (container) =>
  [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
    (element) => element.getClientRects().length > 0
  )

/**
 * Move focus inside the opened panel. The panel slides in with a CSS
 * transition, so a second attempt is made once it has finished.
 * @param {HTMLElement} container
 */
const focusInside = (container) => {
  const focusFirst = () => {
    if (container.contains(document.activeElement)) return

    const focusable =
      container.querySelector('[autofocus]') || getFocusableElements(container)[0]

    if (focusable) {
      focusable.focus()
    } else {
      container.setAttribute('tabindex', '-1')
      container.focus()
    }
  }

  setTimeout(focusFirst, 0)
  setTimeout(focusFirst, 350)
}

/**
 * Keep Tab / Shift+Tab inside the panel while it is open
 * @param {HTMLElement} container
 */
const trapFocus = (container) => {
  removeFocusTrap?.()

  const onKeydown = (e) => {
    if (e.key !== 'Tab' || container.dataset.active !== 'true') return

    const focusable = getFocusableElements(container)
    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!container.contains(document.activeElement)) {
      e.preventDefault()
      first.focus()
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  document.addEventListener('keydown', onKeydown)
  removeFocusTrap = () => {
    document.removeEventListener('keydown', onKeydown)
    removeFocusTrap = null
  }
}

/**
 * Release the trap and give focus back to the control that opened the panel
 */
const restoreFocus = () => {
  removeFocusTrap?.()

  const opener = overlayOpener && overlayOpener.isConnected ? overlayOpener : lastTrigger
  overlayOpener = null

  if (opener && opener.isConnected) opener.focus()
}

export const initCartAccessibility = () => {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const cart = $Q('#shopify-section-side-cart')

      if (cart && cart.dataset.active === 'true') {
        cart.dataset.active = 'false'

        overlayActions?.({
          isActive: true,
          target: cart
        })
        restoreFocus()
      }
    }
  })
}

/**
 * Open nodes with CSS and dataset
 * @param {String} node selector who detect click
 * @param {String} query selector with the node to open
 * @param {Object} config Obejct with attribute from config open
 *
 * @return {Function}
 */

export const toggleDataActive = (node, parent, config) => {
  const { overlay } = config ? config : { overlay: false }
  const toOpen = parent ? $Q(parent) : $Q(node)

  const handler = (e) => {
    if (e.type === 'click') {
      lastTrigger = e.currentTarget
      dataToggle(toOpen, overlay)
    }

    if (e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      lastTrigger = e.currentTarget
      dataToggle(toOpen, overlay)
    }
  }

  const element = $Q(node)

  element.addEventListener('click', handler)
  element.addEventListener('keydown', handler)
}

export function dataToggle(nodeToOpen, overlay) {
  const dataset = nodeToOpen.dataset.active
  const isActive = dataset != 'true' ? false : true

  if (!dataset) return null

  if (!isActive) {
    nodeToOpen.dataset.active = 'true'

    if (overlay) {
      // Opened from a control outside the panel: give focus back to it on close
      if (!overlayOpener && lastTrigger && !nodeToOpen.contains(lastTrigger)) {
        overlayOpener = lastTrigger
      }
      trapFocus(nodeToOpen)
      focusInside(nodeToOpen)
    } else {
      setTimeout(() => {
        const focusable =
          nodeToOpen.querySelector('[autofocus]') ||
          nodeToOpen.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')

        if (focusable) {
          focusable.focus()
        } else {
          nodeToOpen.setAttribute('tabindex', '-1')
          nodeToOpen.focus()
        }
      }, 0)
    }

  } else {
    nodeToOpen.dataset.active = 'false'

    overlay && restoreFocus()
  }

  overlay && overlayActions({
    isActive,
    target: nodeToOpen
  })
}

const overlayActions = ({ isActive, target }) => {
  const id = target.getAttribute('id')

  if (!isActive) {
    const before = document.createElement('div')
    before.setAttribute('id', `overlay--${id}`)
    before.classList.add('overlay')
    target.parentNode.insertBefore(before, target)
    toggleDataActive(
      `#overlay--${id}`,
      `#${id}`,
      { overlay: true }
    )
  } else {
    target.parentNode.removeChild($Q(`#overlay--${id}`))
  }
}
